import { Activity, Building2, CheckCircle, HardDrive, Pause, ScrollText, Users } from "lucide-react";
import { useOverview } from "@/api/hooks";

export function DashboardPage() {
    const overview = useOverview();

    return (
        <div className="mx-auto max-w-5xl">
            <header className="mb-6">
                <h1 className="text-2xl font-semibold">Dashboard</h1>
                <p className="text-sm text-muted-foreground">
                    Tenant admin overview for the Athavita Enterprise Suite.
                </p>
            </header>

            {overview.isPending ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
            ) : overview.isError ? (
                <p className="text-sm text-destructive">{overview.error?.message}</p>
            ) : (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <Metric
                        icon={<Building2 className="h-5 w-5 text-primary" />}
                        label="Tenants"
                        value={overview.data.tenantCount}
                        sub={`${overview.data.activeTenantCount} active${overview.data.suspendedTenantCount > 0 ? ` · ${overview.data.suspendedTenantCount} suspended` : ""}`}
                    />
                    <Metric
                        icon={<Users className="h-5 w-5 text-primary" />}
                        label="Users"
                        value={overview.data.userCount}
                    />
                    <Metric
                        icon={<HardDrive className="h-5 w-5 text-primary" />}
                        label="Mail domains"
                        value={overview.data.domainCount}
                        sub={overview.data.verifiedDomainCount === overview.data.domainCount
                            ? "all verified"
                            : `${overview.data.verifiedDomainCount} verified · ${overview.data.domainCount - overview.data.verifiedDomainCount} pending`}
                        subTone={overview.data.verifiedDomainCount === overview.data.domainCount ? "good" : "warn"}
                        subIcon={overview.data.verifiedDomainCount === overview.data.domainCount
                            ? <CheckCircle className="h-3 w-3" />
                            : <Pause className="h-3 w-3" />}
                    />
                    <Metric
                        icon={<Activity className="h-5 w-5 text-primary" />}
                        label="Audit events (24 h)"
                        value={overview.data.audit24hCount}
                        sub={overview.data.audit24hCount === 0 ? "quiet day" : "rolling window"}
                    />
                </div>
            )}
            <p className="mt-6 text-xs text-muted-foreground">
                <ScrollText className="mr-1 inline h-3 w-3" />
                Every change is recorded in the audit log.
            </p>
        </div>
    );
}

function Metric({
    icon,
    label,
    value,
    sub,
    subTone,
    subIcon
}: {
    icon: React.ReactNode;
    label: string;
    value: number | string;
    sub?: string;
    subTone?: "good" | "warn" | "bad";
    subIcon?: React.ReactNode;
}) {
    const subClass =
        subTone === "good" ? "text-emerald-600 dark:text-emerald-400"
        : subTone === "warn" ? "text-amber-600 dark:text-amber-400"
        : subTone === "bad" ? "text-destructive"
        : "text-muted-foreground";
    return (
        <section className="rounded-lg border bg-card p-4 shadow-sm">
            <div className="mb-2 flex items-center gap-2 text-sm text-muted-foreground">
                {icon}
                {label}
            </div>
            <div className="text-2xl font-semibold tabular-nums">{value}</div>
            {sub ? (
                <div className={`mt-1 flex items-center gap-1 text-xs ${subClass}`}>
                    {subIcon}
                    {sub}
                </div>
            ) : null}
        </section>
    );
}
